import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Connects to data-controller="sortable"
export default class extends Controller {
  static values = {
    url: String,
    params: Object,
    scope: String,
    idsParam: String,
    group: String, // Sortable group name for cross-list dragging
    taskId: Number, // Current task ID (null for dashboard)
    objectiveId: Number, // Objective ID for cross-task validation
    draggable: String, // CSS selector for draggable items (defaults to [data-sortable-item])
  };

  // Allow parent controllers to temporarily disable/enable this instance
  disable() {
    try {
      if (this.sortable) this.sortable.option("disabled", true);
    } catch (_) {
      /* no-op */
    }
  }

  enable() {
    try {
      if (this.sortable) this.sortable.option("disabled", false);
    } catch (_) {
      /* no-op */
    }
  }

  connect() {
    this.element.dataset.sortableAnimation = "150";
    this.element.dataset.sortableTouch = "true";
    this.element.dataset.sortableDelayOnTouch = "true";

    // Avoid initializing on Sortable clones during drag (items with children)
    if (this.isInDraggedContext()) return;

    this.initializeSortable();

    // Add CSS classes for sorting states and keyboard handlers
    this.addSortingStyles();
    this.addKeyboardReorder();

    // Listen for Turbo Stream events to reinitialize when list changes
    this.boundReinitialize = this.reinitialize.bind(this);
    document.addEventListener("turbo:frame-load", this.boundReinitialize);
    document.addEventListener(
      "turbo:before-stream-render",
      this.boundReinitialize,
    );

    // Listen for disclosure toggle events (when objectives/tasks expand/collapse)
    this.boundHandleDisclosure = this.handleDisclosure.bind(this);
    document.addEventListener(
      "objective-disclosure:toggled",
      this.boundHandleDisclosure,
    );
  }

  initializeSortable() {
    // Prevent concurrent reinitialization
    if (this._initializing) return;
    this._initializing = true;

    // If element is detached or in a dragged/ghost context, skip
    if (!this.element.isConnected || this.isInDraggedContext()) {
      this._initializing = false;
      return;
    }

    // Destroy existing instance if present
    if (this.sortable) {
      try {
        // Sometimes Sortable already nulled the element during a prior destroy
        if (this.sortable.el && this.sortable.el.isConnected) {
          this.sortable.destroy();
        }
      } catch (e) {
        console.warn("Failed to destroy Sortable instance:", e);
      } finally {
        this.sortable = null;
      }
    }

    // Use provided group or default to 'quick-todos'
    const groupName = this.hasGroupValue ? this.groupValue : "quick-todos";

    const draggableSelector = this.hasDraggableValue
      ? this.draggableValue
      : "[data-sortable-item]";

    this.sortable = Sortable.create(this.element, {
      group: groupName,
      handle: "[data-sortable-handle]",
      draggable: draggableSelector,
      // Improve nested/complex DOM dragging stability
      fallbackOnBody: true,
      forceFallback: true,
      fallbackTolerance: 3, // Start drag after 3px movement for smoother feel
      dragoverBubble: true,
      swapThreshold: 0.5, // Swap earlier for smoother transitions
      invertSwap: true,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      animation: 150,
      onMove: this.onMove.bind(this),
      onStart: this.onStart.bind(this),
      onEnd: this.onEnd.bind(this),
    });

    this._initializing = false;
  }

  reinitialize(event) {
    // Only reinitialize if the event affects this element or its children
    if (!event || !event.target) return;

    // Check if the stream target is this element or a descendant
    const target = event.target;
    if (
      (target === this.element || this.element.contains(target)) &&
      !this.isInDraggedContext()
    ) {
      // Small delay to let DOM settle after Turbo update
      setTimeout(() => {
        this.initializeSortable();
      }, 50);
    }
  }

  handleDisclosure(event) {
    // When an objective/task disclosure is toggled and content is shown,
    // reinitialize sortable if this element is inside the toggled content
    if (!event || !event.detail) return;

    const toggledElement = event.detail.element;
    const expanded = event.detail.expanded;

    // Only reinitialize if content was just expanded and contains this sortable
    if (expanded && toggledElement && toggledElement.contains(this.element)) {
      // Delay to let disclosure animation complete
      setTimeout(() => {
        this.initializeSortable();
      }, 250); // Slightly longer than the 200ms disclosure animation
    }
  }

  disconnect() {
    if (this.sortable) {
      try {
        if (this.sortable.el && this.sortable.el.isConnected) {
          this.sortable.destroy();
        }
      } catch (e) {
        console.warn("Failed to destroy Sortable instance on disconnect:", e);
      } finally {
        this.sortable = null;
      }
    }

    // Remove event listeners
    if (this.boundReinitialize) {
      document.removeEventListener("turbo:frame-load", this.boundReinitialize);
      document.removeEventListener(
        "turbo:before-stream-render",
        this.boundReinitialize,
      );
    }
    if (this.boundHandleDisclosure) {
      document.removeEventListener(
        "objective-disclosure:toggled",
        this.boundHandleDisclosure,
      );
    }
  }

  // Returns true if this controller's element is inside a currently dragged
  // or ghosted item (common when dragging parents that contain nested sortables)
  isInDraggedContext() {
    return !!this.element.closest(
      ".sortable-ghost, .sortable-drag, .sortable-chosen",
    );
  }

  onStart(event) {
    // mark saving state disabled and show indicator immediately
    this.setDisabled(true);
    this.toggleSavingIndicator(true);

    // Auto-expand collapsed tasks when dragging todos over them
    // This allows dropping todos into collapsed task lists
    // Only activate for todo items, not when reordering tasks themselves
    if (event.item.classList.contains("todo-item")) {
      this._setupAutoExpandOnDragOver();
    }

    // Programmatically disable any nested Sortable controllers inside the dragged item
    // to ensure they cannot capture dragover/drop during a parent drag
    try {
      this._nestedDisabledCtrls = [];
      const nested = event.item.querySelectorAll(
        '[data-controller~="sortable"], [data-controller~="objective-sortable"]',
      );
      nested.forEach((el) => {
        const ids = ["sortable", "objective-sortable"];
        ids.forEach((id) => {
          const ctrl = this.application?.getControllerForElementAndIdentifier?.(
            el,
            id,
          );
          if (ctrl && typeof ctrl.disable === "function") {
            ctrl.disable();
            this._nestedDisabledCtrls.push(ctrl);
          } else {
            // Fallback: minimize interference if controller API not found
            el.style.pointerEvents = "none";
            el.setAttribute("data-nested-sortable-disabled", "true");
          }
        });
      });
    } catch (_) {
      /* no-op */
    }

    // As an additional hardening step, detach nested sortables within the dragged item
    // and insert placeholders to preserve layout; reattach onEnd
    try {
      this._detachedNested = [];
      const nestedLists = event.item.querySelectorAll(
        '[data-controller~="sortable"], [data-controller~="objective-sortable"]',
      );
      nestedLists.forEach((el) => {
        // Skip if the nested element is the list item itself (should not happen)
        if (el === this.element) return;
        const placeholder = document.createElement("div");
        placeholder.className = "nested-sortable-placeholder";
        // Preserve height to avoid layout jump
        placeholder.style.height = `${el.offsetHeight}px`;
        placeholder.style.width = "100%";
        placeholder.setAttribute("aria-hidden", "true");
        el.parentNode.insertBefore(placeholder, el);
        this._detachedNested.push({ el, placeholder });
        el.remove();
      });
    } catch (_) {
      /* no-op */
    }
  }

  // Show visual drop indicator on collapsed tasks or empty expanded tasks when dragging todos
  _setupAutoExpandOnDragOver() {
    this._lastHoveredTask = null;
    this._dropIndicator = null;
    this._isEmptyExpandedTask = false;

    this._boundDragOverHandler = (e) => {
      // Find the closest task element (task-row) being hovered
      const elementUnderCursor = document.elementFromPoint(
        e.clientX,
        e.clientY,
      );
      const taskElement = elementUnderCursor?.closest(".task-row");

      // Clean up indicator if not hovering a task
      if (!taskElement) {
        this._removeDropIndicator();
        this._lastHoveredTask = null;
        this._isEmptyExpandedTask = false;
        return;
      }

      // Skip if we're already hovering this task
      if (taskElement === this._lastHoveredTask) return;

      this._removeDropIndicator();
      this._lastHoveredTask = taskElement;
      this._isEmptyExpandedTask = false;

      // Check if the task is collapsed (has disclosure controller and not expanded)
      const disclosureCtrl =
        this.application?.getControllerForElementAndIdentifier?.(
          taskElement,
          "objective-disclosure",
        );

      // Show drop indicator for:
      // 1. Collapsed tasks
      // 2. Expanded tasks with no todos (empty drop zone is hard to target)
      if (disclosureCtrl && !disclosureCtrl.expanded) {
        this._showDropIndicator(taskElement);
      } else if (disclosureCtrl && disclosureCtrl.expanded) {
        // Check if the task has no todos
        const todoList = taskElement.querySelector('[data-controller~="sortable"]');
        const todos = todoList?.querySelectorAll('.todo-item, [data-sortable-item]') || [];
        if (todos.length === 0) {
          this._isEmptyExpandedTask = true;
          this._showDropIndicator(taskElement);
        }
      }
    };

    // Use mousemove since it works with SortableJS fallback mode
    document.addEventListener("mousemove", this._boundDragOverHandler);
    // Note: Drop handling is done in onEnd() which intercepts the drop before SortableJS reverts
  }

  _showDropIndicator(taskElement) {
    // Check if task is collapsed or expanded
    const disclosureCtrl = this.application?.getControllerForElementAndIdentifier?.(
      taskElement,
      "objective-disclosure",
    );
    const isExpanded = disclosureCtrl?.expanded;

    // Create the drop indicator element
    const indicatorHtml = `
      <div class="flex items-center justify-center py-2 px-3 bg-blue-50 dark:bg-blue-900/30 border-2 border-dashed border-blue-400 dark:border-blue-500 rounded text-blue-600 dark:text-blue-400 text-xs font-medium opacity-60">
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        Drop here
      </div>
    `;

    if (isExpanded) {
      // For expanded tasks: insert inside the todo list (where todos appear)
      this._dropIndicator = document.createElement("li");
      this._dropIndicator.className = "todo-drop-indicator";
      this._dropIndicator.innerHTML = indicatorHtml;

      const contentArea = taskElement.querySelector('[data-objective-disclosure-target="content"]');
      if (contentArea) {
        const todoList = contentArea.querySelector('.todo-list-component > ul, ul[data-controller~="sortable"]');
        if (todoList) {
          todoList.prepend(this._dropIndicator);
          return;
        }
        contentArea.prepend(this._dropIndicator);
        return;
      }
    } else {
      // For collapsed tasks: insert after the task progress info (visible area)
      this._dropIndicator = document.createElement("div");
      this._dropIndicator.className = "todo-drop-indicator mt-2";
      this._dropIndicator.innerHTML = indicatorHtml;

      // Find the details row area (where progress/count is shown)
      const detailsRow = taskElement.querySelector('.flex.items-start.gap-2.mt-1');
      if (detailsRow) {
        // Insert after the details row
        detailsRow.after(this._dropIndicator);
        return;
      }
    }

    // Fallback: append to task element
    taskElement.appendChild(this._dropIndicator);
  }

  _removeDropIndicator() {
    if (this._dropIndicator) {
      this._dropIndicator.remove();
      this._dropIndicator = null;
    }
  }

  async _handleDropOnCollapsedTask(
    todoElement,
    targetTaskId,
    taskElement,
    disclosureCtrl,
    event,
  ) {
    const todoId = todoElement.dataset.id;
    const sourceTaskId = event.from.dataset.taskId;
    const sourceObjectiveId = event.from.dataset.objectiveId;

    // Clean up UI first
    this.cleanupDragClasses();
    this._cleanupAutoExpandListeners();

    if (!todoId || !targetTaskId) {
      this.toggleSavingIndicator(false);
      this.setDisabled(false);
      return;
    }

    try {
      // Keep saving indicator visible during API call
      this.toggleSavingIndicator(true);

      // Determine if cross-objective move
      const targetObjectiveId =
        taskElement.closest("[data-objective-id]")?.dataset?.objectiveId ||
        taskElement.closest("[data-sortable-objective-id-value]")?.dataset
          ?.sortableObjectiveIdValue;

      const isCrossObjective =
        sourceObjectiveId &&
        targetObjectiveId &&
        sourceObjectiveId !== targetObjectiveId;

      let url, method, body;
      if (isCrossObjective) {
        // Cross-objective move
        url = `/objectives/${sourceObjectiveId}/tasks/${sourceTaskId}/todos/${todoId}/move`;
        method = "POST";
        body = JSON.stringify({ target_task_id: targetTaskId });
      } else {
        // Same objective move - use timestamp for unique position to avoid conflicts
        const uniquePosition = (Date.now() % 10000000) + 1;
        url = `/objectives/${sourceObjectiveId}/tasks/${sourceTaskId}/todos/${todoId}`;
        method = "PATCH";
        body = JSON.stringify({
          todo: { task_id: targetTaskId, position: uniquePosition },
        });
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
          Accept: "application/json",
        },
        body: body,
      });

      if (response.ok) {
        // Remove the todo from the UI
        todoElement.remove();

        // Expand the target task to show the new todo (if collapsed)
        if (disclosureCtrl && !disclosureCtrl.expanded) {
          disclosureCtrl.expand();
        }

        // Show success message
        if (typeof window.toast === "function") {
          window.toast("Todo moved successfully", { type: "success" });
        }

        // Refresh the page to show updated state
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to move todo");
      }
    } catch (error) {
      console.error("Error moving todo to collapsed task:", error);
      if (typeof window.toast === "function") {
        window.toast("Failed to move todo", { type: "error" });
      }
    } finally {
      this.toggleSavingIndicator(false);
      this.setDisabled(false);
    }
  }

  _cleanupAutoExpandListeners() {
    this._removeDropIndicator();
    this._lastHoveredTask = null;
    this._isEmptyExpandedTask = false;
    if (this._boundDragOverHandler) {
      document.removeEventListener("mousemove", this._boundDragOverHandler);
      this._boundDragOverHandler = null;
    }
  }

  async onEnd(event) {
    const item = event.item;
    const itemId = item.dataset.id;
    const oldTaskId = event.from.dataset.taskId
      ? parseInt(event.from.dataset.taskId)
      : null;
    const newTaskId = event.to.dataset.taskId
      ? parseInt(event.to.dataset.taskId)
      : null;
    const oldObjectiveId = event.from.dataset.objectiveId
      ? parseInt(event.from.dataset.objectiveId)
      : null;
    const newObjectiveId = event.to.dataset.objectiveId
      ? parseInt(event.to.dataset.objectiveId)
      : null;
    const newPosition = event.newIndex + 1;

    // Check for drop on collapsed task or empty expanded task FIRST, before any other logic
    // This handles the case where user drags a todo over a task that needs special drop handling
    if (
      this._lastHoveredTask &&
      this._dropIndicator &&
      item.classList.contains("todo-item")
    ) {
      const taskElement = this._lastHoveredTask;
      const targetTaskId = taskElement.dataset.id;

      // Check if this is a valid drop target (collapsed OR empty expanded)
      const disclosureCtrl =
        this.application?.getControllerForElementAndIdentifier?.(
          taskElement,
          "objective-disclosure",
        );

      const isCollapsed = disclosureCtrl && !disclosureCtrl.expanded;
      const isEmptyExpanded = this._isEmptyExpandedTask;

      if ((isCollapsed || isEmptyExpanded) && targetTaskId) {
        // Handle the drop on collapsed/empty task
        await this._handleDropOnCollapsedTask(
          item,
          targetTaskId,
          taskElement,
          disclosureCtrl,
          event,
        );
        return;
      }
    }

    // Detect list types
    const isTodoList = oldTaskId !== null || newTaskId !== null;
    const isTaskList =
      !isTodoList && (oldObjectiveId !== null || newObjectiveId !== null);

    // Detect if this is a cross-task move (todos)
    const isCrossTaskMove = isTodoList && oldTaskId !== newTaskId;
    const isCrossObjectiveTodoMove =
      isTodoList &&
      oldObjectiveId !== null &&
      newObjectiveId !== null &&
      oldObjectiveId !== newObjectiveId;
    const isCrossObjectiveTaskMove =
      isTaskList &&
      oldObjectiveId !== null &&
      newObjectiveId !== null &&
      oldObjectiveId !== newObjectiveId;

    // If no change in position or task, clean up and exit
    if (
      !isCrossTaskMove &&
      !isCrossObjectiveTaskMove &&
      !isCrossObjectiveTodoMove &&
      event.oldIndex === event.newIndex
    ) {
      this.cleanupDragClasses();
      this._cleanupAutoExpandListeners();
      this.toggleSavingIndicator(false);
      this.setDisabled(false);
      return;
    }

    // Show saving indicator
    this.toggleSavingIndicator(true);
    this.setDisabled(true);

    try {
      if (isTodoList) {
        if (isCrossObjectiveTodoMove) {
          // Move todo across objectives using dedicated move endpoint, then reorder destination
          await this.moveTodoAcrossObjectives(
            itemId,
            oldObjectiveId,
            oldTaskId,
            newTaskId,
          );
          await this.reorderDestinationTask(event.to);
        } else if (isCrossTaskMove && this.hasTaskIdValue) {
          // Cross-task move within same objective: update task_id then reorder destination
          await this.updateTodoTask(itemId, oldTaskId, newTaskId, newPosition);
          await this.reorderDestinationTask(event.to);
        } else {
          // Same-list reorder (todos)
          await this.reorderList(event);
        }
      } else if (isTaskList) {
        if (isCrossObjectiveTaskMove) {
          // Move task to another objective within the same project, then reorder destination
          await this.moveTaskToObjective(
            itemId,
            oldObjectiveId,
            newObjectiveId,
          );
          await this.reorderDestinationObjectiveTasks(event.to);
        } else {
          // Same-objective reorder (tasks)
          await this.reorderList(event);
        }
      } else {
        // Fallback: standard reorder
        await this.reorderList(event);
      }

      // Update data-position attributes in new order (only items matching draggable selector)
      const itemsAfter = this.itemsForReorder();
      itemsAfter.forEach((el, idx) => {
        el.dataset.position = String(idx + 1);
      });
    } catch (error) {
      console.error("Reorder failed:", error);
      // Revert the move on error
      this.revertSort(event);
      this.showError(error.message);
    } finally {
      // Ensure any leftover drag classes are removed to avoid greyed-out items
      this.cleanupDragClasses();
      // Reattach and re-enable any nested sortable lists we disabled/detached on drag start
      try {
        // First, reattach any nested sortables we detached at drag start
        if (this._detachedNested && this._detachedNested.length) {
          this._detachedNested.forEach(({ el, placeholder }) => {
            try {
              // Cleanup any fallback disable styles before reattaching
              el.style.pointerEvents = "";
              el.removeAttribute &&
                el.removeAttribute("data-nested-sortable-disabled");
              if (placeholder && placeholder.parentNode) {
                placeholder.parentNode.insertBefore(el, placeholder);
                placeholder.remove();
              }
              // If a controller is already attached, ensure it's enabled; otherwise Stimulus will connect
              const ids = ["sortable", "objective-sortable"];
              ids.forEach((id) => {
                const ctrl =
                  this.application?.getControllerForElementAndIdentifier?.(
                    el,
                    id,
                  );
                if (ctrl && typeof ctrl.enable === "function") {
                  try {
                    ctrl.enable();
                  } catch (_) {
                    /* no-op */
                  }
                }
              });
            } catch (_) {
              /* no-op */
            }
          });
        }
        this._detachedNested = [];

        // Also cleanup any fallback styles left on elements still within the dragged item
        const nestedEls = event.item.querySelectorAll(
          "[data-nested-sortable-disabled]",
        );
        nestedEls.forEach((el) => {
          el.style.pointerEvents = "";
          el.removeAttribute("data-nested-sortable-disabled");
        });

        // Legacy references are no longer reliable after detach/reattach
        this._nestedDisabledCtrls = [];
      } catch (_) {
        /* no-op */
      }

      // Clean up auto-expand listeners
      this._cleanupAutoExpandListeners();

      this.toggleSavingIndicator(false);
      this.setDisabled(false);

      // Dispatch custom event for parent controllers to hook into
      this.element.dispatchEvent(
        new CustomEvent("sortable:end", {
          bubbles: true,
          detail: { event, items: this.itemsForReorder() },
        }),
      );
    }
  }

  // Prevent dropping into nested sortable containers within the dragged item.
  // This addresses a common nested-list issue where elementFromPoint resolves to
  // a sortable inside the dragged element, causing invalid/cancelled drops.
  onMove(evt) {
    try {
      const { to, dragged } = evt || {};
      // Strictly disallow dropping into a container nested inside the dragged element
      if (to && dragged && dragged.contains(to)) return false;

      // If moving across lists, only allow if destination list shares the same group
      const rootEl = this.element;
      const thisGroup = this.hasGroupValue ? this.groupValue : "quick-todos";
      if (to && to !== rootEl) {
        const toGroup =
          to.dataset.sortableGroupValue ||
          to.getAttribute("data-sortable-group-value");
        // If destination has a group different from ours, or lacks a group entirely, disallow
        if (!toGroup || toGroup !== thisGroup) return false;
      }
    } catch (_) {
      /* ignore */
    }
    return true;
  }

  async updateTodoTask(todoId, oldTaskId, newTaskId, newPosition) {
    // Build URL using the OLD task (where todo currently lives, not where it's going)
    // The controller uses the URL to authorize access, then uses task_id from params to update
    const url = `/objectives/${this.objectiveIdValue}/tasks/${oldTaskId}/todos/${todoId}`;

    // Use timestamp-based position to avoid uniqueness conflicts when moving between tasks
    // The subsequent reorder call will set the correct position
    const tempPosition = Date.now() % 10000000;

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({
        todo: {
          task_id: newTaskId,
          position: tempPosition,
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }

    return response;
  }

  async moveTodoAcrossObjectives(todoId, oldObjectiveId, oldTaskId, newTaskId) {
    // Use the dedicated move endpoint; keep URL scoped to current location for authorization
    const url = `/objectives/${oldObjectiveId}/tasks/${oldTaskId}/todos/${todoId}/move`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ target_task_id: newTaskId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }
    return response;
  }

  async reorderList(event) {
    // If no URL is configured, skip the request (parent controller handles it)
    if (!this.hasUrlValue || !this.urlValue) {
      return;
    }

    const item = event.item;
    const itemId = item.dataset.id;
    const newPosition = event.newIndex + 1;

    // Use the configured draggable selector to collect items in order
    const items = this.itemsForReorder();
    const ids = items.map((item) => item.dataset.id);

    // Prepare the request data
    let baseParams = {};
    try {
      if (
        this.hasParamsValue &&
        this.paramsValue &&
        typeof this.paramsValue === "object"
      ) {
        baseParams = this.paramsValue;
      }
    } catch (e) {
      // Ignore malformed params; default to empty params
      baseParams = {};
    }
    const data = { ...baseParams };

    // Use custom ids parameter name if provided, otherwise fall back to ordered_ids
    const idsParamName = this.idsParamValue || "ordered_ids";
    data[idsParamName] = ids;

    // Add legacy parameters for backwards compatibility
    data.item_id = itemId;
    data.new_position = newPosition;
    data.ordered_ids = ids;

    // Add scope if provided
    if (this.scopeValue) {
      data.scope = this.scopeValue;
    }

    await this.sendReorderRequest(data);
  }

  async reorderDestinationTask(destinationElement) {
    // Get the task ID from the destination element
    const taskId = destinationElement.dataset.taskId;
    if (!taskId) return;

    // Get all items in the destination list in their new order
    const items = Array.from(
      destinationElement.querySelectorAll("[data-sortable-item]"),
    );
    const ids = items.map((item) => item.dataset.id);

    // Use the API endpoint for reordering
    const response = await fetch("/api/v1/todos/reorder", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({
        task_id: taskId,
        ordered_ids: ids,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  async moveTaskToObjective(taskId, oldObjectiveId, newObjectiveId) {
    // API endpoint to move a task between objectives (within same project)
    const url = `/api/v1/tasks/${taskId}/move`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({ objective_id: newObjectiveId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
    }
    return response;
  }

  async reorderDestinationObjectiveTasks(destinationElement) {
    const objectiveId = destinationElement.dataset.objectiveId;
    if (!objectiveId) return;

    // Get all items in the destination objective in their new order
    const selector =
      destinationElement.getAttribute("data-sortable-draggable-value") ||
      "[data-sortable-item]";
    const items = Array.from(
      destinationElement.querySelectorAll(selector),
    ).filter(
      (el) =>
        el.closest('[data-controller~="sortable"]') === destinationElement,
    );
    const ids = items.map((item) => item.dataset.id);

    const response = await fetch("/api/v1/tasks/reorder", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json",
      },
      body: JSON.stringify({
        objective_id: objectiveId,
        ordered_ids: ids,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  }

  async sendReorderRequest(data) {
    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCSRFToken(),
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;
      try {
        const payload = await response.json();
        if (payload && payload.error) {
          message += ` - ${payload.error}`;
        }
      } catch (_) {
        // ignore JSON parse errors for non-JSON responses
      }
      throw new Error(message);
    }

    return response;
  }

  revertSort(event) {
    // Move the item back to its original position
    const item = event.item;
    const items = Array.from(this.element.children);

    if (event.oldIndex < items.length) {
      const target = items[event.oldIndex];
      this.element.insertBefore(item, target);
    } else {
      this.element.appendChild(item);
    }
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute("content") : "";
  }

  addSortingStyles() {
    // Only inject handle cursor styles - main sortable styles are in application.css
    const style = document.createElement("style");
    style.textContent = `
      .sortable-ghost,
      .sortable-chosen,
      .sortable-drag {
        list-style: none !important;
      }

      [data-sortable-handle] {
        cursor: grab;
      }

      [data-sortable-handle]:active {
        cursor: grabbing;
      }

      .sortable-chosen [data-sortable-handle] {
        cursor: grabbing !important;
      }
    `;

    if (!document.querySelector("#sortable-styles")) {
      style.id = "sortable-styles";
      document.head.appendChild(style);
    }
  }

  addKeyboardReorder() {
    this.element.addEventListener("keydown", async (e) => {
      if (!e.altKey) return;
      if (!(e.key === "ArrowDown" || e.key === "ArrowUp")) return;

      const focused = document.activeElement;
      if (!focused || !focused.matches("[data-sortable-item]")) return;

      e.preventDefault();
      const items = this.itemsForReorder();
      const index = items.indexOf(focused);
      if (index === -1) return;

      let newIndex = index;
      if (e.key === "ArrowDown" && index < items.length - 1)
        newIndex = index + 1;
      if (e.key === "ArrowUp" && index > 0) newIndex = index - 1;
      if (newIndex === index) return;

      const moving = items[index];
      const reference =
        e.key === "ArrowDown" ? items[index + 1].nextSibling : items[index - 1];
      this.element.insertBefore(moving, reference);

      const ids = this.itemsForReorder().map((el) => el.dataset.id);
      this.toggleSavingIndicator(true);
      this.setDisabled(true);
      try {
        let baseParamsKb = {};
        try {
          if (
            this.hasParamsValue &&
            this.paramsValue &&
            typeof this.paramsValue === "object"
          ) {
            baseParamsKb = this.paramsValue;
          }
        } catch (e) {
          baseParamsKb = {};
        }
        const data = { ...baseParamsKb };
        const idsParamName = this.idsParamValue || "ordered_ids";
        data[idsParamName] = ids;
        await this.sendReorderRequest(data);
        // Update positions
        this.itemsForReorder().forEach(
          (el, i) => (el.dataset.position = String(i + 1)),
        );
      } catch (err) {
        console.error(err);
      } finally {
        this.toggleSavingIndicator(false);
        this.setDisabled(false);
      }
    });
  }

  toggleSavingIndicator(show) {
    const container =
      this.element.closest("[data-quick-todos-container]") || document;
    const indicator = container.querySelector(".saving-indicator");
    if (indicator) {
      if (show) {
        indicator.classList.remove("hidden");
      } else {
        indicator.classList.add("hidden");
      }
    }
  }

  setDisabled(disabled) {
    const container =
      this.element.closest("[data-quick-todos-container]") || this.element;
    container.setAttribute(
      "data-sortable-disabled",
      disabled ? "true" : "false",
    );
  }

  showError(message) {
    if (typeof window.toast === "function") {
      window.toast(message || "Failed to move todo", { type: "error" });
    }
  }

  cleanupDragClasses() {
    try {
      const classes = ["sortable-ghost", "sortable-chosen", "sortable-drag"];
      classes.forEach((cls) => {
        this.element
          .querySelectorAll(`.${cls}`)
          .forEach((el) => el.classList.remove(cls));
      });
    } catch (e) {
      // no-op
    }
  }

  // Helper to get items that are considered for ordering based on configured selector
  itemsForReorder() {
    const selector = this.hasDraggableValue
      ? this.draggableValue
      : "[data-sortable-item]";
    // Use element.querySelectorAll with selector and then filter to only those whose
    // closest list container is this.element (avoid nested list items)
    const all = Array.from(this.element.querySelectorAll(selector));
    return all.filter(
      (el) => el.closest('[data-controller~="sortable"]') === this.element,
    );
  }
}
